import React, {useEffect, useState} from 'react'
import {Box, Button, Grid, Typography} from '@mui/material'
import DataGrid from '../../../common/ResponsiveDataGrid'
import {useGetStatusWiseWeightDiscrepancyQuery} from '../../../redux/commonRTK'
import {formatDate, formatNumber} from '../../../helpers/UtilityHelper'
import Loader from '../../../common/Loader'
import {LoadingButton} from '@mui/lab'
import {FormProvider, RHFSelectField, RHFTextField} from '../../../common/hook-form'
import {useForm} from 'react-hook-form'
import _ from 'lodash'
import * as yup from 'yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {filterBarSx, primaryBtnSx, secondaryBtnSx} from '../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../styles/foundation/colors'

const WeightDiscrepancyAnalytics = ({merchantId}) => {
  const [filter, setFilter] = useState(null)

  const wdStaus = {
    new: 'NEW',
    acceptedByCourier: 'ACCEPTED_BY_COURIER',
    acceptedByMerchant: 'ACCEPTED_BY_MERCHANT',
    disputedByCourier: 'DISPUTED_BY_COURIER',
    disputedByMerchant: 'DISPUTED_BY_MERCHANT',
    autoAcceptedBySystem: 'AUTO_ACCEPTED_BY_SYSTEM',
    rejectedProship: 'REJECTED_PROSHIP',
    acceptedProship: 'ACCEPTED_PROSHIP',
  }

  const currentYear = new Date().getFullYear()
  const currentMonth = new Date().getMonth() + 1

  useEffect(() => {
    // Set default filter to current month and year
    setFilter(getFirstAndLastDate(currentYear, currentMonth))
    setValue('month', currentMonth)
    setValue('year', currentYear.toString())

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const payload = {
    collection: 'weight_discrepancy',
    andfilter: [
      {field: 'merchantId', operator: 'eq', value: merchantId},
      {
        field: 'status',
        operator: 'in',
        value: [
          'NEW',
          'ACCEPTED_BY_COURIER',
          'ACCEPTED_BY_MERCHANT',
          'DISPUTED_BY_COURIER',
          'DISPUTED_BY_MERCHANT',
          'AUTO_ACCEPTED_BY_SYSTEM',
          'REJECTED_PROSHIP',
          'ACCEPTED_PROSHIP',
        ],
      },
      {field: 'createdDate', operator: 'gte', value: filter?.firstDate},
      {field: 'createdDate', operator: 'lte', value: filter?.lastDate},
    ],
  }
  const {
    data: weightDiscrepancyData,
    isLoading,
    refetch,
    isFetching,
  } = useGetStatusWiseWeightDiscrepancyQuery(payload, {
    skip: !filter?.firstDate || !filter?.lastDate,
  })

  const months = [
    {label: 'January', Value: 1},
    {label: 'February', Value: 2},
    {label: 'March', Value: 3},
    {label: 'April', Value: 4},
    {label: 'May', Value: 5},
    {label: 'June', Value: 6},
    {label: 'July', Value: 7},
    {label: 'August', Value: 8},
    {label: 'September', Value: 9},
    {label: 'October', Value: 10},
    {label: 'November', Value: 11},
    {label: 'December', Value: 12},
  ]

  // Single API call

  function getFirstAndLastDate(year, month) {
    const firstDate = new Date(year, month - 1, 1)
    const lastDate = new Date(year, month, 0) // Last day of the current month
    const monthName = firstDate.toLocaleString('default', {month: 'long'}) // Get full month name

    return {
      firstDate: `Date('${formatDate(firstDate, 'yyyy-MM-dd')} 00:00:00')`, // Format: YYYY-MM-DD
      lastDate: `Date('${formatDate(lastDate, 'yyyy-MM-dd')} 23:59:59')`,
      monthNameYear: `${monthName} ${year}`, // Example: "August 2024"
    }
  }

  const getStatusCount = (status) => {
    if (!weightDiscrepancyData?.[0]?.StatusCounts) return 0
    const statusData = weightDiscrepancyData[0].StatusCounts.find((item) => item._id === status)
    return statusData ? statusData.count : 0
  }

  const rows = [
    {
      id: 1,
      label: 'New',
      value: getStatusCount(wdStaus.new) || '-',
    },
    {
      id: 2,
      label: 'Accepted By Courier',
      value: getStatusCount(wdStaus.acceptedByCourier) || '-',
    },
    {
      id: 3,
      label: 'Accepted By Merchant',
      value: getStatusCount(wdStaus.acceptedByMerchant) || '-',
    },
    {
      id: 4,
      label: 'Disputed By Courier',
      value: getStatusCount(wdStaus.disputedByCourier) || '-',
    },
    {
      id: 5,
      label: 'Disputed By Merchant',
      value: getStatusCount(wdStaus.disputedByMerchant) || '-',
    },
    {
      id: 6,
      label: 'Auto Accepted By System',
      value: getStatusCount(wdStaus.autoAcceptedBySystem) || '-',
    },
    {
      id: 7,
      label: 'Rejected Proship',
      value: getStatusCount(wdStaus.rejectedProship) || '-',
    },
    {
      id: 8,
      label: 'Accepted Proship',
      value: getStatusCount(wdStaus.acceptedProship) || '-',
    },

    {
      id: 9,
      label: (
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: '#d32f2f'}}>
          Monetary Impact
        </Typography>
      ),
      value: (
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: '#d32f2f'}}>
          {formatNumber(weightDiscrepancyData?.[0]?.['Monetary Impact'] || 0)}
        </Typography>
      ),
    },
  ]

  const columns = [
    {
      field: 'label',
      headerName: 'Status Wise Discrepancy & Monetary Impact',
      flex: 1.5,
      renderCell: (params) => <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: NAVY}}>{params.value}</Typography>,
    },
    {
      field: 'value',
      headerName: 'Value',
      flex: 1,
      renderCell: (params) => <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.semibold, fontSize: SIZE['2xs'], color: NAVY}}>{params.value}</Typography>,
    },
  ]
  const schema = yup.object().shape({
    month: yup.string().required('Month is required'),
    year: yup
      .string()
      .required('Year is required')
      .matches(/^\d{4}$/, 'Year must be a 4-digit number')
      .test('year', `Year must be between 2000 and ${currentYear}`, (value) => {
        const year = parseInt(value)
        return year >= 2000 && year <= currentYear
      }),
  })

  const methods = useForm({
    defaultValues: {
      year: '',
      month: '',
    },
    resolver: yupResolver(schema),
  })
  const {handleSubmit, setValue} = methods

  const onSubmit = (data) => {
    if (data.year && data.month) {
      setFilter(getFirstAndLastDate(data.year, data.month))
    }
  }

  useEffect(() => {
    if (filter) {
      refetch() // Manually refetch data when filter changes
    }
  }, [filter, refetch])

  return (
    <>
      {/* <PageTitle>Channel Order Analytics</PageTitle> */}
      {isFetching && <Loader />}
      <Box sx={filterBarSx}>
        <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.base, color: NAVY, mb: 0.5}}>
          Monthly Weight Discrepancy Insights: <Typography component='span' sx={{color: '#d32f2f', fontWeight: WEIGHT.bold}}>{filter?.monthNameYear || '-'}</Typography>
        </Typography>
        <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE}}>
          Select or change the month using the filter below.
        </Typography>
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} sx={{mt: 1}}>
            <Grid item xs={3}>
              <RHFSelectField
                name='month'
                label='Select Month'
                size='small'
                options={_.orderBy(months, 'name').map((val) => ({
                  value: val.Value,
                  label: val.label,
                }))}
                width='100%'
              />
            </Grid>
            <Grid item xs={3}>
              <RHFTextField name='year' label='Year' size='small' />
            </Grid>
            <Grid item xs={6}>
              <LoadingButton
                loading={isLoading}
                size='small'
                variant='contained'
                type='submit'
                sx={{...primaryBtnSx, mt: 1}}
              >
                Submit
              </LoadingButton>
              <Button
                size='small'
                variant='outlined'
                sx={{...secondaryBtnSx, mt: 1, ml: 1}}
                onClick={() => {
                  setValue('month', currentMonth)
                  setValue('year', currentYear.toString())
                  setFilter(getFirstAndLastDate(currentYear.toString(), currentMonth.toString()))
                }}
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </FormProvider>
      </Box>

      <Box sx={{mt: 2, fontFamily: FONT, '& .MuiDataGrid-root': {border: 'none', fontFamily: FONT, fontSize: SIZE['2xs']}, '& .MuiDataGrid-columnHeaders': {bgcolor: '#fafafa'}}}>
        <DataGrid
          rows={rows}
          columns={columns}
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          hideFooterPagination
          autoHeight
        />
      </Box>
    </>
  )
}

export default WeightDiscrepancyAnalytics
